import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { QuickPlayService, QuickPlaySettings } from '../../services/quick-play.service';
import { GenreSelectionComponent } from '../genre-selection/genre-selection.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SpeechRecognitionService } from '../../../../core/services/speech-recognition.service';

@Component({
  selector: 'app-quick-play-panel',
  templateUrl: './quick-play-panel.component.html',
  styleUrls: ['./quick-play-panel.component.scss']
})
export class QuickPlayPanelComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  settings!: QuickPlaySettings;
  loading = false;
  voiceEnabled = false;
  
  // Quick access genres (displayed as buttons)
  quickGenres: string[] = [];

  constructor(
    private quickPlayService: QuickPlayService,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private speechService: SpeechRecognitionService
  ) {}

  ngOnInit(): void {
    // Check voice support
    this.voiceEnabled = this.speechService.isSupported();
    
    // Subscribe to settings changes
    this.quickPlayService.settings$
      .pipe(takeUntil(this.destroy$))
      .subscribe(settings => {
        this.settings = settings;
        this.updateQuickGenres();
      });

    // Subscribe to loading state
    this.quickPlayService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.loading = loading;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateQuickGenres(): void {
    // Show up to 5 selected genres as quick access buttons
    this.quickGenres = this.settings.selectedGenres.slice(0, 5);
  }

  async onQuickPlay(genre: string): Promise<void> {
    if (this.loading) return;

    try {
      const result = await this.quickPlayService.quickPlay(genre);
      
      if (result?.playlist) {
        this.snackBar.open(
          `AI generated ${genre} playlist created and opened!`, 
          'Close', 
          { 
            duration: 3000,
            horizontalPosition: 'end',
            verticalPosition: 'bottom',
            panelClass: ['success-snackbar']
          }
        );
        // Navigation is now handled in the service
      }
    } catch (error: any) {
      console.error('Quick play error:', error);
      const message = error.message || error.error?.message || 'Failed to create AI playlist';
      this.snackBar.open(message, 'Close', { 
        duration: 5000,
        panelClass: ['error-snackbar']
      });
    }
  }

  openGenreSelection(): void {
    const dialogRef = this.dialog.open(GenreSelectionComponent, {
      width: '500px',
      maxHeight: '80vh',
      data: { selectedGenres: this.settings.selectedGenres }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.genres) {
        const updatedSettings = {
          ...this.settings,
          selectedGenres: result.genres
        };
        this.quickPlayService.saveSettings(updatedSettings);
      }
    });
  }

  async onCreateNewPlaylist(): Promise<void> {
    if (this.loading) return;

    // Use a native prompt dialog for the freeform input — keeps the touch
    // surface tiny and avoids a new dialog component.
    const prompt = window.prompt('Describe the playlist you want:');
    if (!prompt || !prompt.trim()) return;

    try {
      const result = await this.quickPlayService.createNewDynamicPlaylist(prompt);
      if (result) {
        this.snackBar.open(`"${result.name}" created`, 'Close', {
          duration: 3000,
          horizontalPosition: 'end',
          verticalPosition: 'bottom',
          panelClass: ['success-snackbar']
        });
      }
    } catch (error: any) {
      console.error('Create new playlist error:', error);
      const message = error?.message || error?.error?.message || 'Failed to create playlist';
      this.snackBar.open(message, 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
    }
  }


  getGenreIcon(genre: string): string {
    const iconMap: { [key: string]: string } = {
      'pop': 'star',
      'rock': 'electric_guitar',
      'electronic': 'equalizer',
      'hip-hop': 'mic',
      'jazz': 'music_note',
      'classical': 'piano',
      'indie': 'headphones',
      'dance': 'nightlife',
      'chill': 'self_improvement',
      'party': 'celebration',
      'focus': 'psychology',
      'workout': 'fitness_center'
    };
    
    return iconMap[genre.toLowerCase()] || 'music_note';
  }

  getGenreDisplayName(genre: string): string {
    return genre.split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  showVoiceHelp(): void {
    const helpText = `
Voice Commands Help:

Examples of voice commands you can use:
• "Generate a playlist based on AC/DC Thunderstruck"
• "Create a workout playlist with rock music"
• "Make a chill playlist for studying"
• "Generate happy music for the morning"
• "Create a 90s alternative rock playlist"

Simply click the microphone button and speak your command clearly.
    `;
    
    this.snackBar.open(helpText, 'Close', {
      duration: 10000,
      panelClass: ['info-snackbar']
    });
  }
}